import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen, Sunrise, Sun, Moon, Flame, Dumbbell, Timer,
  Search, X, Leaf, Egg, ChevronDown, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 12;

const DietBadge = ({ type, size = "sm" }) => {
  if (!type) return null;
  const styles = {
    Vegan: "bg-green-600 text-white",
    Veg: "bg-green-500 text-white",
    Egg: "bg-amber-500 text-white",
    "Non-Veg": "bg-red-500 text-white",
  };
  const dotStyles = {
    Vegan: "bg-green-200", Veg: "bg-green-200", Egg: "bg-amber-200", "Non-Veg": "bg-red-200",
  };
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded ${size === "sm" ? "text-[9px]" : "text-[10px]"} font-bold ${styles[type] || styles.Veg}`}>
      <span className={`w-1.5 h-1.5 rounded-sm ${dotStyles[type] || dotStyles.Veg}`} />
      {type}
    </span>
  );
};

const isNewlyAdded = (meal) => {
  if (!meal.created_at) return false;
  const created = new Date(meal.created_at);
  return (Date.now() - created.getTime()) < 24 * 60 * 60 * 1000;
};

const LibraryMealCard = ({ meal, onClick }) => (
  <div className="bg-white rounded-2xl border border-border/40 overflow-hidden hover:shadow-md transition-all cursor-pointer" 
    onClick={onClick} data-testid={`library-meal-${meal.meal_id}`}>
    <div className="aspect-[4/3] overflow-hidden relative">
      <img src={meal.image_url} alt={meal.meal_name}
        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy"
        onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400"; }} />
      <div className="absolute top-2.5 left-2.5 flex gap-1">
        <span className="px-2 py-0.5 rounded-full bg-white/90 backdrop-blur-sm text-[10px] font-bold uppercase tracking-wide text-primary">
          {meal.primary_meal_type}
        </span>
        <DietBadge type={meal.diet_type} />
        {meal.is_gluten_free && <span className="px-1.5 py-0.5 rounded-full bg-teal-500 text-white text-[9px] font-bold">GF</span>}
      </div>
      {isNewlyAdded(meal) && (
        <div className="absolute top-2.5 right-2.5">
          <span className="px-2 py-0.5 rounded-full bg-primary text-white text-[9px] font-bold shadow-sm" data-testid={`new-badge-${meal.meal_id}`}>
            Newly Added
          </span>
        </div>
      )}
    </div>
    <div className="p-3.5">
      <h3 className="font-bold text-sm text-foreground mb-1.5 truncate" style={{ fontFamily: 'Playfair Display, serif' }}>
        {meal.meal_name}
      </h3>
      <div className="flex items-center gap-2.5 text-[11px] text-muted-foreground">
        {meal.calories > 0 && <span className="flex items-center gap-0.5"><Flame size={11} className="text-orange-500" />{meal.calories}</span>}
        {meal.protein > 0 && <span className="flex items-center gap-0.5"><Dumbbell size={11} className="text-blue-500" />{meal.protein}g</span>}
        <span className="flex items-center gap-0.5"><Timer size={11} />{meal.prep_time_min}m</span>
      </div>
    </div>
  </div>
);

const LibraryPage = () => {
  const [meals, setMeals] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeFilter, setActiveFilter] = useState("all");
  const [dietFilter, setDietFilter] = useState("all");
  const [gfFilter, setGfFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const searchTimeout = useRef(null);

  const buildParams = useCallback((pageNum) => {
    const params = { page: pageNum, limit: PAGE_SIZE };
    if (activeFilter !== "all") params.meal_type = activeFilter;
    if (dietFilter !== "all") params.diet_type = dietFilter;
    if (gfFilter) params.gluten_free = true;
    if (searchQuery) params.search = searchQuery;
    return params;
  }, [activeFilter, dietFilter, gfFilter, searchQuery]);

  // Fetch meals
  const fetchMeals = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const params = buildParams(pageNum);
      const [mealsRes, countRes] = await Promise.all([
        axios.get(`${API}/meals`, { params }),
        axios.get(`${API}/meals/count`, { params: { ...params, page: undefined, limit: undefined } })
      ]);

      if (append) setMeals(prev => [...prev, ...mealsRes.data]);
      else setMeals(mealsRes.data);

      setTotal(countRes.data.total);
      setPage(pageNum);
    } catch (e) {
      console.error("Error:", e);
      toast.error("Couldn't load meals");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildParams]);

  // Initial + filter change
  useEffect(() => { fetchMeals(1); }, [activeFilter, dietFilter, gfFilter, searchQuery, fetchMeals]);

  // Debounced search
  const handleSearchInput = (val) => {
    setSearchInput(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearchQuery(val), 400);
  };

  const hasMore = meals.length < total;

  const FilterPill = ({ active, onClick, children }) => (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
        ${active ? "bg-primary text-white shadow-sm" : "bg-muted/40 text-muted-foreground hover:bg-muted/70"}`}>
      {children}
    </button>
  );

  if (loading) {
    return (
      <div className="app-container py-16 flex flex-col items-center justify-center min-h-[60vh]" data-testid="library-loading">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-5 animate-pulse">
          <BookOpen size={28} className="text-primary" strokeWidth={1.5} />
        </div>
        <p className="text-sm text-muted-foreground">Loading meals...</p>
      </div>
    );
  }

  return (
    <div className="app-container py-6 md:py-10" data-testid="library-page">
      {/* Header */}
      <section className="mb-6 animate-fade-in-up">
        <h1 className="heading-display text-2xl md:text-3xl mb-1">Meal Library</h1>
        <p className="text-sm text-muted-foreground">{total} meals available</p>
      </section>

      {/* Search */}
      <section className="mb-4 animate-fade-in-up">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/50" />
          <input type="text" placeholder="Search meals..."
            value={searchInput} onChange={(e) => handleSearchInput(e.target.value)}
            className="w-full pl-9 pr-8 py-2.5 text-sm bg-white border border-border/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20"
            data-testid="library-search" />
          {searchInput && (
            <button onClick={() => { setSearchInput(""); setSearchQuery(""); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-foreground">
              <X size={14} />
            </button>
          )}
        </div>
      </section>

      {/* Filters */}
      <section className="mb-5 space-y-2.5 animate-fade-in-up">
        {/* Meal type */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          <FilterPill active={activeFilter === "all"} onClick={() => setActiveFilter("all")}>All</FilterPill>
          <FilterPill active={activeFilter === "breakfast"} onClick={() => setActiveFilter("breakfast")}>
            <Sunrise size={12} className="inline mr-1" />Breakfast
          </FilterPill>
          <FilterPill active={activeFilter === "lunch"} onClick={() => setActiveFilter("lunch")}>
            <Sun size={12} className="inline mr-1" />Lunch
          </FilterPill>
          <FilterPill active={activeFilter === "dinner"} onClick={() => setActiveFilter("dinner")}>
            <Moon size={12} className="inline mr-1" />Dinner
          </FilterPill>
        </div>
        {/* Diet + GF */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          <FilterPill active={dietFilter === "all"} onClick={() => setDietFilter("all")}>All diets</FilterPill>
          <FilterPill active={dietFilter === "Vegan"} onClick={() => setDietFilter("Vegan")}>Vegan</FilterPill>
          <FilterPill active={dietFilter === "Veg"} onClick={() => setDietFilter("Veg")}>Veg</FilterPill>
          <FilterPill active={dietFilter === "Egg"} onClick={() => setDietFilter("Egg")}>Egg</FilterPill>
          <FilterPill active={dietFilter === "Non-Veg"} onClick={() => setDietFilter("Non-Veg")}>Non-Veg</FilterPill>
          <FilterPill active={gfFilter} onClick={() => setGfFilter(!gfFilter)}>
            {gfFilter ? "GF" : "Gluten-Free"}
          </FilterPill>
        </div>
      </section>

      {/* Grid */}
      <section className="animate-fade-in-up">
        {meals.length === 0 ? (
          <div className="text-center py-16" data-testid="no-meals">
            <p className="text-muted-foreground text-sm">No meals found</p>
            {(searchQuery || dietFilter !== "all" || gfFilter) && (
              <Button variant="outline" size="sm" className="mt-3 text-xs"
                onClick={() => { setSearchInput(""); setSearchQuery(""); setDietFilter("all"); setGfFilter(false); setActiveFilter("all"); }}>
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {meals.map((meal, i) => (
                <div key={meal.id || meal.meal_id} className="animate-fade-in" style={{ animationDelay: `${(i % PAGE_SIZE) * 30}ms` }}>
                  <LibraryMealCard meal={meal} onClick={() => navigate(`/meal/${meal.meal_id}`, { state: { meal, from: "library" } })} />
                </div>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button variant="outline" size="sm" onClick={() => fetchMeals(page + 1, true)}
                  disabled={loadingMore} className="gap-1.5 text-xs rounded-full px-6"
                  data-testid="load-more-btn">
                  {loadingMore ? <><Loader2 size={12} className="animate-spin" />Loading...</> : <><ChevronDown size={12} />Load more ({total - meals.length} remaining)</>}
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default LibraryPage;
